// 약관 문서 관리(/emp/legal) 화면들이 공유하는 라벨·헬퍼입니다.
// 이 기능의 API는 기존 관리자 API(snake_case)와 달리 전부 camelCase 이고, 시각은 전부 UTC 입니다.

import { formatUtcToTimeZone, formatLocalToUtc } from "@/utils/datetime";

// 서버 시각은 UTC 로 오고 화면은 KST 로 보여줍니다.
// 관리자가 해외에서 접속해도 "시행일"이 흔들리지 않도록 브라우저 로컬이 아닌 고정 타임존을 씁니다.
export const LEGAL_TZ = "Asia/Seoul";

// 본문 입력 언어. src/lang/lang.js 가 지원하는 5개 언어와 동일하게 유지합니다.
export const LEGAL_LANGS = [
  { code: "ko", label: "한국어" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
];

export const DOC_TYPES = [
  { value: "terms", label: "이용약관" },
  { value: "privacy", label: "개인정보처리방침" },
  { value: "marketing", label: "마케팅 수신동의" },
  { value: "nda", label: "보안서약" },
  { value: "age", label: "연령 확인" },
  { value: "etc", label: "기타" },
];

export const TARGET_USER_TYPES = [
  { value: "all", label: "전체" },
  { value: "learner", label: "학생" },
  { value: "instructor", label: "강사" },
];

// 동의가 수집된 경로. 분쟁 대응 시 "어느 화면에서 받은 동의인가"를 판별합니다.
export const CONSENT_SOURCES = [
  { value: "signup", label: "회원가입" },
  { value: "oauth-signup", label: "소셜 가입" },
  { value: "mypage", label: "마이페이지" },
  { value: "reconsent", label: "재동의" },
  { value: "nda", label: "강사 보안서약" },
];

// status 별 표시. 버튼 노출 조건은 canEdit/canDelete/... 헬퍼로 판정합니다.
export const VERSION_STATUS = {
  draft: { label: "초안", pill: "yellow" },
  published: { label: "발행됨", pill: "ok" },
  archived: { label: "보관", pill: "default" },
};

const labelOf = (list, value) => list.find((x) => x.value === value)?.label || value || "-";

export const docTypeLabel = (v) => labelOf(DOC_TYPES, v);
export const targetUserTypeLabel = (v) => labelOf(TARGET_USER_TYPES, v);
export const consentSourceLabel = (v) => labelOf(CONSENT_SOURCES, v);
export const langLabel = (code) => LEGAL_LANGS.find((l) => l.code === code)?.label || code;

/**
 * UTC ISO 문자열을 KST 표시용 문자열로 바꿉니다.
 * @param {string} iso 서버가 준 UTC 시각
 * @param {{ withTime?: boolean }} opts withTime=false 면 날짜만
 * @returns {string} "2026-09-06 09:00" / "2026-09-06" / 값이 없으면 "-"
 */
export const fmtKst = (iso, { withTime = true } = {}) => {
  if (!iso) return "-";
  const s = formatUtcToTimeZone(iso, LEGAL_TZ); // YYYY-MM-DDTHH:mm:ss
  if (!s) return "-";
  return withTime ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : s.slice(0, 10);
};

/**
 * UTC ISO → <input type="datetime-local"> 값 (KST 기준)
 * @param {string} iso
 * @returns {string} "YYYY-MM-DDTHH:mm" (값이 없으면 빈 문자열)
 */
export const toKstInput = (iso) => {
  if (!iso) return "";
  return formatUtcToTimeZone(iso, LEGAL_TZ).slice(0, 16);
};

/**
 * <input type="datetime-local"> 값(KST) → 서버로 보낼 UTC ISO
 * @param {string} localValue "YYYY-MM-DDTHH:mm"
 * @returns {string} "YYYY-MM-DDTHH:mm:ssZ" (값이 없으면 빈 문자열)
 */
export const kstInputToUtc = (localValue) => {
  if (!localValue) return "";
  return formatLocalToUtc(localValue, LEGAL_TZ);
};

/**
 * 시행일이 아직 오지 않았는지 판정합니다. (사전 고지 발행 → "시행 예정" 표시)
 * @param {string} iso UTC ISO
 */
export const isFuture = (iso) => {
  if (!iso) return false;
  const t = Date.parse(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return !Number.isNaN(t) && t > Date.now();
};

/**
 * 기존 버전 목록에서 다음 버전 번호를 제안합니다. ("1.0" → "1.1", 없으면 "1.0")
 * 어디까지나 입력 편의용이고, 실제 중복 판정은 서버(409)가 합니다.
 * @param {Array<{version: string}>} versions
 */
export const suggestNextVersion = (versions = []) => {
  const nums = versions
    .map((v) => String(v.version).match(/^(\d+)\.(\d+)$/))
    .filter(Boolean)
    .map((m) => [Number(m[1]), Number(m[2])]);
  if (!nums.length) return "1.0";
  nums.sort((a, b) => b[0] - a[0] || b[1] - a[1]);
  const [major, minor] = nums[0];
  return `${major}.${minor + 1}`;
};

/**
 * 빈 본문 언어를 걸러낸 contents 객체를 만듭니다.
 * title·content 가 모두 비어있는 언어는 서버로 보내지 않습니다.
 * @param {Object} contents { ko: {title, content}, ... }
 */
export const compactContents = (contents = {}) => {
  const out = {};
  Object.keys(contents).forEach((lang) => {
    const c = contents[lang] || {};
    const title = (c.title || "").trim();
    const content = (c.content || "").trim();
    // Quill 은 내용을 지워도 <p><br></p> 를 남기므로 빈 본문으로 취급합니다.
    const isBlank = !content || content === "<p><br></p>";
    if (!title && isBlank) return;
    out[lang] = { title, content: isBlank ? "" : content };
  });
  return out;
};
